import { makeRng, shuffle } from "../rng";
import { GameError, type GameEvent } from "./types";
import type { PuzzleModule } from "./puzzle";

/**
 * Videnspar – the classic memory game with a school twist.
 *
 * Turn over two cards. Two that match form a pair – but you only get to KEEP the pair by answering a question
 * (from the subjects and grade you picked). A right answer secures the pair (and builds your streak); a wrong answer turns the cards
 * back over – now you know where they are, so you can try again with a new question. Nobody loses: the game ends when all pairs are found.
 */

/** the picture on a card = name of a Lucide icon and a colour (drawn by the client) */
export const MEMORY_SYMBOLS: { icon: string; color: string }[] = [
  { icon: "LuCat", color: "#f59e0b" }, { icon: "LuDog", color: "#a16207" }, { icon: "LuRabbit", color: "#ec4899" }, { icon: "LuFish", color: "#0ea5e9" },
  { icon: "LuBird", color: "#22c55e" }, { icon: "LuTurtle", color: "#16a34a" }, { icon: "LuSquirrel", color: "#c2410c" }, { icon: "LuBug", color: "#dc2626" },
  { icon: "LuSun", color: "#f59e0b" }, { icon: "LuMoon", color: "#6366f1" }, { icon: "LuStar", color: "#eab308" }, { icon: "LuCloud", color: "#38bdf8" },
  { icon: "LuFlame", color: "#ef4444" }, { icon: "LuSnowflake", color: "#06b6d4" }, { icon: "LuTreePine", color: "#15803d" }, { icon: "LuMountain", color: "#64748b" },
  { icon: "LuAnchor", color: "#1d4ed8" }, { icon: "LuCrown", color: "#d97706" }, { icon: "LuKey", color: "#ca8a04" }, { icon: "LuHeart", color: "#e11d48" },
  { icon: "LuRocket", color: "#7c3aed" }, { icon: "LuApple", color: "#dc2626" }, { icon: "LuCastle", color: "#78716c" }, { icon: "LuFlower", color: "#db2777" },
  { icon: "LuIceCreamCone", color: "#f472b6" }, { icon: "LuBike", color: "#0d9488" }, { icon: "LuMusic", color: "#9333ea" }, { icon: "LuUmbrella", color: "#2563eb" },
];

export const pairsForGrade = (grade: number, difficulty: number): number => (grade <= 2 ? 6 : grade <= 5 ? 8 : 10) + (difficulty >= 3 ? 2 : 0);
export const colsFor = (pairs: number): number => (pairs <= 6 ? 4 : pairs <= 8 ? 4 : pairs === 10 ? 5 : 6);

export interface MemoryState {
  faces: number[];
  matched: boolean[];
  up: number[];
  mode: "pick" | "miss" | "ask";
  pairs: number;
  cols: number;
  claimed: number;
  flips: number;
  misses: number;
  retries: number;
  streak: number;
  best: number;
  phase: "play" | "over";
  won: boolean | null;
}

export interface MemoryView {
  /** symbol index per card – or null while the card lies face down */
  cards: (number | null)[];
  matched: boolean[];
  up: number[];
  mode: "pick" | "miss" | "ask";
  pairs: number;
  cols: number;
  claimed: number;
  flips: number;
  misses: number;
  streak: number;
  best: number;
  phase: "play" | "over";
  won: boolean | null;
}

export const videnspar: PuzzleModule<MemoryState, MemoryView> = {
  id: "videnspar",
  questionSec: 40,
  create(seed, o) {
    const rng = makeRng(`${seed}:videnspar`);
    const pairs = pairsForGrade(o.grade, o.difficulty);
    const symbols = shuffle(Array.from({ length: MEMORY_SYMBOLS.length }, (_, i) => i), rng).slice(0, pairs);
    const faces = shuffle([...symbols, ...symbols], rng);
    return {
      faces, matched: faces.map(() => false), up: [], mode: "pick", pairs, cols: colsFor(pairs),
      claimed: 0, flips: 0, misses: 0, retries: 0, streak: 0, best: 0, phase: "play", won: null,
    };
  },
  act(s, body) {
    if (s.phase === "over") throw new GameError("Spillet er slut.");
    switch (body?.type) {
      case "hide":
        if (s.mode === "miss") { s.up = []; s.mode = "pick"; }
        return [];
      case "flip": {
        if (s.mode === "ask") throw new GameError("Svar på spørgsmålet først.");
        const i = body.i;
        if (!Number.isInteger(i) || i < 0 || i >= s.faces.length) throw new GameError("Vælg et kort.");
        if (s.mode === "miss") { s.up = []; s.mode = "pick"; }
        if (s.matched[i]) throw new GameError("Det par er allerede fundet.");
        if (s.up.includes(i)) throw new GameError("Det kort er allerede vendt.");
        s.up.push(i);
        s.flips++;
        if (s.up.length < 2) return [{ kind: "flip", data: { i } }];
        const [a, b] = s.up as [number, number];
        if (s.faces[a] === s.faces[b]) {
          s.mode = "ask";
          return [{ kind: "flip", data: { i } }, { kind: "need-question", data: { purpose: "pair", a, b } }];
        }
        s.mode = "miss";
        s.misses++;
        s.streak = 0;
        return [{ kind: "flip", data: { i } }, { kind: "miss", data: { a, b } }];
      }
    }
    throw new GameError("Ukendt handling.");
  },
  onAnswer(s, correct, _d, purpose) {
    if (purpose !== "pair" || s.mode !== "ask" || s.up.length !== 2) return [];
    const [a, b] = s.up as [number, number];
    s.up = [];
    s.mode = "pick";
    if (!correct) {
      s.retries++;
      s.streak = 0;
      return [{ kind: "again", data: { a, b } }];
    }
    s.matched[a] = s.matched[b] = true;
    s.claimed++;
    s.streak++;
    s.best = Math.max(s.best, s.streak);
    const ev: GameEvent[] = [{ kind: "pair", data: { a, b, face: s.faces[a], streak: s.streak } }];
    if (s.claimed >= s.pairs) {
      s.phase = "over";
      s.won = true;
      ev.push({ kind: "win" });
    }
    return ev;
  },
  view(s) {
    const visible = (i: number) => s.matched[i] || s.up.includes(i);
    return {
      cards: s.faces.map((f, i) => (visible(i) ? f : null)), matched: s.matched.slice(), up: s.up.slice(), mode: s.mode, pairs: s.pairs, cols: s.cols,
      claimed: s.claimed, flips: s.flips, misses: s.misses, streak: s.streak, best: s.best, phase: s.phase, won: s.won,
    };
  },
  score: (s) => Math.max(0, s.claimed * 100 + s.best * 15 - s.misses * 4 - s.retries * 10 + (s.won ? 150 : 0)),
  over: (s) => s.phase === "over",
  won: (s) => s.won === true,
};
